import { useState, useEffect, useContext } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import "./Programs.css";
import ProgramCard from "./ProgramCard";
import PatientCalendar from "./PatientCalendar";
import Loader from "@shared/components/Loader";
import http from "@shared/api/http";
import ProfileCard from "./ProfileCard";
import { useTranslation } from "react-i18next";
import useLocalStorage from "@shared/hooks/useLocalStorage";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import RequestProgramModal from "@features/programs/modals/RequestProgramModal";
import { canRequestNewProgram } from "@features/programs/services/notificationService";
import { WeekBand } from "./gamification";
import InstallBanner from "./InstallBanner";
import { BrandingContext } from "@shared/branding/BrandingContext";

const headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
};
const PROGRAMS_PER_PAGE = 10;

function Programs() {
  const { t } = useTranslation();
  const { brandName } = useContext(BrandingContext);
  // Use custom hook for persisting view preference
  const [currentView, setCurrentView] = useLocalStorage('program-view-preference', 'list'); // "list" or "calendar"
  
  // Request modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [page, setPage] = useState(1);
  const [canRequest, setCanRequest] = useState(true);
  const [daysUntilNext, setDaysUntilNext] = useState(0);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  
  // Programs with their sessions and nothing else: the list needs no
  // exercises, sources or account data (see V2::ProgramsController#summary).
  const url = "/:locale/v2/:token/programs/summary";
  const [programs, setPrograms] = useState({
    loading: false,
    data: null,
    error: false,
  });

  let content = null;

  useEffect(() => {
    setPrograms({
      loading: true,
      data: null,
      error: false,
    });
    http
      .get(url, {
        headers: headers,
      })
      .then((response) => {
        setPrograms({
          loading: false,
          data: response.data,
          error: false,
        });
      })
      .catch(() => {
        setPrograms({
          loading: false,
          data: null,
          error: true,
        });
      });
  }, [url]);

  // Check eligibility only when there are no programs
  useEffect(() => {
    if (programs.data && programs.data.length === 0) {
      checkEligibility();
    }
  }, [programs.data]);

  const checkEligibility = async () => {
    setIsCheckingEligibility(true);
    try {
      const response = await canRequestNewProgram();
      setCanRequest(response.can_request);
      setDaysUntilNext(response.days_until_next);
    } catch (error) {
      console.error("Error checking eligibility:", error);
      setCanRequest(true); // Fallback to allowing request
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleRequestNewProgram = () => {
    setShowRequestModal(true);
  };

  const handleRequestSuccess = () => {
    setShowRequestModal(false);
  };

  if (programs.error) {
    content = <p>Ein Fehler ist aufgetreten. Bitte versuche es noch einmal</p>;
  }

  if (programs.loading) {
    content = <Loader></Loader>;
  }

  if (programs.data) {
    // outsource sort func to utils
    let sorted_programs = programs.data
      ?.map((obj) => {
        return { ...obj, created_at: new Date(obj.created_at) };
      })
      .sort((a, b) => b.created_at - a.created_at);

    /* Every program of the patient arrives in one response. Paging the
       rendered list keeps a long history readable; moving the paging into the
       request needs the endpoint to accept a page parameter. */
    const page_count = Math.max(
      1,
      Math.ceil((sorted_programs?.length || 0) / PROGRAMS_PER_PAGE)
    );
    const current_page = Math.min(page, page_count);
    const visible_programs = (sorted_programs || []).slice(
      (current_page - 1) * PROGRAMS_PER_PAGE,
      current_page * PROGRAMS_PER_PAGE
    );
      
    content = (
      <Container fluid className="program-overview-container">
        <Row className="profile-card-mobile">
          <ProfileCard />
        </Row>
        
        {/* View Toggle */}
        <Row className="align-items-center mb-3">
          <Col xs={12} md={8} className="d-flex align-items-center">
            <div className="breadcrumb-content">
              <h4>{currentView === "list" ? t("program.overview") : t("calendar.overview")}</h4>
            </div>
          </Col>
          <Col xs={12} md={4} className="d-flex justify-content-end align-items-center">
            <div className="view-toggle-container">
              <button 
                className={`view-toggle-btn ${currentView === "list" ? "active" : ""}`}
                onClick={() => setCurrentView("list")}
              >
                {t("patients.listView")}
              </button>
              <button 
                className={`view-toggle-btn ${currentView === "calendar" ? "active" : ""}`}
                onClick={() => setCurrentView("calendar")}
              >
                {t("patients.calendarView")}
              </button>
            </div>
          </Col>
        </Row>

        {/* Content based on current view */}
        {currentView === "list" ? (
          <>
            {sorted_programs?.length > 0 ? (
              <>
                {/* This week across every active program, computed from the
                    sessions the list already carries. */}
                <InstallBanner app={brandName} />
                <div className="program-week-band">
                  <WeekBand programs={sorted_programs} />
                </div>
                {visible_programs.map((program, key) => (
                  <ProgramCard program={program} id={key} key={key} />
                ))}
                {page_count > 1 && (
                  <nav className="program-pager">
                    <button
                      type="button"
                      className="program-pager-btn"
                      onClick={() => setPage(current_page - 1)}
                      disabled={current_page === 1}
                    >
                      ← {t("program.previous")}
                    </button>
                    <span className="program-pager-status">
                      {t("program.pageOf", {
                        page: current_page,
                        pages: page_count,
                      })}
                    </span>
                    <button
                      type="button"
                      className="program-pager-btn"
                      onClick={() => setPage(current_page + 1)}
                      disabled={current_page === page_count}
                    >
                      {t("program.next")} →
                    </button>
                  </nav>
                )}
                <div className="fake-program-card"></div>
              </>
            ) : (
              <Row className="no-programs-container">
                <Col xs={12} className="text-center py-5">
                  <div className="no-programs-icon">📋</div>
                  <h4 className="mt-3 mb-3">{t("program.no_programs_available")}</h4>
                  <p className="text-muted mb-4">{t("program.no_programs_description")}</p>
                  {canRequest ? (
                    <Button 
                      variant="primary" 
                      onClick={handleRequestNewProgram}
                      className="request-program-btn"
                      disabled={isCheckingEligibility}
                    >
                      {isCheckingEligibility ? t("actions.loading") : t("program.request_new_program")}
                    </Button>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="request-tooltip">
                          {daysUntilNext === 1 
                            ? t("program.request_cooldown_single")
                            : t("program.request_cooldown", { days: daysUntilNext })
                          }
                        </Tooltip>
                      }
                    >
                      <span className="d-inline-block">
                        <Button 
                          variant="primary" 
                          disabled
                          className="request-program-btn"
                          style={{ pointerEvents: 'none' }}
                        >
                          {t("program.request_new_program")}
                        </Button>
                      </span>
                    </OverlayTrigger>
                  )}
                </Col>
              </Row>
            )}
          </>
        ) : (
          <PatientCalendar />
        )}
      </Container>
    );
  }

  return (
    <div>
      {content}
      
      {/* Request New Program Modal */}
      <RequestProgramModal 
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        programId={null}
        onSuccess={handleRequestSuccess}
      />
    </div>
  );
}

export default Programs;
